import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import EngineerLayout from "../components/EngineerLayout";

const WORK_LOCATION_OPTIONS = ["Office", "Workshop", "Store", "Training", "Meeting", "Other"];

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const formatAddress = (address) => {
  if (!address) return "-";
  if (typeof address === "string") return address;

  return [address.street, address.area, address.city, address.state, address.pincode]
    .filter(Boolean)
    .join(", ");
};

export default function DailyUpdate() {
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerEmail = localStorage.getItem("engineerEmail") || "";
  const engineerId = localStorage.getItem("engineerId") || "";
  const engineerLookup =
    engineerId ||
    engineerEmail ||
    engineerName;

  const [entryType, setEntryType] = useState("non-complaint");
  const [workDate, setWorkDate] = useState(todayInputValue());
  const [complaints, setComplaints] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    complaintId: "",
    workMode: "",
    workLocationType: "",
    workLocationName: "",
    workDescription: ""
  });

  const engineerMatches = useMemo(
    () =>
      [engineerName, engineerEmail, engineerId, engineerLookup]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [engineerEmail, engineerId, engineerLookup, engineerName]
  );

  const fetchPageData = useCallback(async (selectedDate = workDate) => {
    try {
      setLoading(true);
      const [complaintsResult, customersResult, updatesResult] = await Promise.allSettled([
        axios.get(API_BASE_URL + "/api/complaints"),
        axios.get(API_BASE_URL + "/api/customers"),
        axios.get(API_BASE_URL + "/api/daily-updates", {
          params: { engineer: engineerLookup, workDate: selectedDate }
        })
      ]);

      const complaintData =
        complaintsResult.status === "fulfilled" && Array.isArray(complaintsResult.value.data)
          ? complaintsResult.value.data
          : [];

      setComplaints(
        complaintData.filter((complaint) =>
          engineerMatches.includes(String(complaint.engineerAssigned || "").trim().toLowerCase())
        )
      );

      setCustomers(
        customersResult.status === "fulfilled" && Array.isArray(customersResult.value.data)
          ? customersResult.value.data
          : []
      );

      setUpdates(
        updatesResult.status === "fulfilled" && Array.isArray(updatesResult.value.data)
          ? updatesResult.value.data
          : []
      );
    } catch (error) {
      console.error("Failed to load daily update page", error);
      setComplaints([]);
      setCustomers([]);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  }, [engineerLookup, engineerMatches, workDate]);

  useEffect(() => {
    if (!engineerLookup) return;
    fetchPageData(workDate);
  }, [engineerLookup, workDate, fetchPageData]);

  const customerMap = useMemo(
    () => new Map(customers.map((customer) => [String(customer._id), customer])),
    [customers]
  );

  const complaintOptions = useMemo(
    () =>
      complaints
        .filter((complaint) => String(complaint.status || "").toLowerCase() !== "completed")
        .map((complaint) => {
          const customer = customerMap.get(String(complaint.customerId));
          return {
            ...complaint,
            destination: formatAddress(customer?.address),
            city: customer?.address?.city || ""
          };
        }),
    [complaints, customerMap]
  );

  const selectedComplaint = complaintOptions.find((complaint) => complaint._id === form.complaintId);

  const resetForm = () => {
    setForm({
      complaintId: "",
      workMode: "",
      workLocationType: "",
      workLocationName: "",
      workDescription: ""
    });
  };

  const submitUpdate = async () => {
    if (entryType === "complaint" && !selectedComplaint) {
      alert("Please choose a complaint visit");
      return;
    }

    if (entryType === "non-complaint" && !form.workDescription.trim()) {
      alert("Please enter the work update details");
      return;
    }

    if (entryType === "non-complaint" && !form.workMode.trim()) {
      alert("Please enter the work type");
      return;
    }

    try {
      setSaving(true);

      const payload =
        entryType === "complaint"
          ? {
              engineerId: engineerLookup,
              engineerName,
              entryType,
              workDate,
              complaintId: selectedComplaint._id,
              complaintRefId: selectedComplaint.complaintId,
              customerId: selectedComplaint.customerId,
              company: selectedComplaint.company,
              location: selectedComplaint.destination,
              city: selectedComplaint.city,
              workDescription: form.workDescription
            }
          : {
              engineerId: engineerLookup,
              engineerName,
              entryType,
              workDate,
              workMode: form.workMode,
              workLocationType: form.workLocationType,
              workLocationName: form.workLocationName,
              location: form.workLocationName || form.workLocationType,
              city: form.workLocationName,
              workDescription: form.workDescription
            };

      await axios.post(API_BASE_URL + "/api/daily-updates", payload);
      resetForm();
      await fetchPageData(workDate);
    } catch (error) {
      if (error.response?.status === 404) {
        alert("Daily Update backend route is not available yet. Please restart the backend server that is running on port 5000.");
      } else {
        alert(error.response?.data?.message || "Failed to save daily update");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <EngineerLayout>
      <div className="mobile-dense-page mx-auto max-w-6xl space-y-4 lg:space-y-6">
        <section className="rounded-[30px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_34%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(224,231,255,0.78))] p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-700">Daily Update</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Work Log</h1>
            </div>

            <div className="rounded-2xl border border-sky-100 bg-white/80 px-4 py-3 text-sm text-slate-700">
              Engineer: <span className="font-semibold text-slate-900">{engineerName || engineerLookup || "-"}</span>
            </div>
          </div>
        </section>

        <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid gap-4 lg:grid-cols-[auto_220px] lg:items-end">
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setEntryType("non-complaint")}
                className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                  entryType === "non-complaint" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                General Work
              </button>
              <button
                type="button"
                onClick={() => setEntryType("complaint")}
                className={`rounded-2xl px-4 py-2.5 text-sm font-semibold transition ${
                  entryType === "complaint" ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                Complaint Visit
              </button>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">Work Date</label>
              <input
                type="date"
                value={workDate}
                onChange={(event) => setWorkDate(event.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
              />
            </div>
          </div>

          {entryType === "complaint" ? (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Complaint</label>
                <select
                  value={form.complaintId}
                  onChange={(event) => setForm((current) => ({ ...current, complaintId: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                >
                  <option value="">Select complaint</option>
                  {complaintOptions.map((complaint) => (
                    <option key={complaint._id} value={complaint._id}>
                      {complaint.complaintId} - {complaint.company} - {complaint.destination}
                    </option>
                  ))}
                </select>
              </div>

              <ReadOnlyField label="Location" value={selectedComplaint?.destination || "-"} />
              <ReadOnlyField label="City" value={selectedComplaint?.city || "-"} />
            </div>
          ) : (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Work Type</label>
                <input
                  value={form.workMode}
                  onChange={(event) => setForm((current) => ({ ...current, workMode: event.target.value }))}
                  placeholder="Office work, workshop work, stock check..."
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Location Type</label>
                <select
                  value={form.workLocationType}
                  onChange={(event) => setForm((current) => ({ ...current, workLocationType: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                >
                  <option value="">Select location type</option>
                  {WORK_LOCATION_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Location Name</label>
                <input
                  value={form.workLocationName}
                  onChange={(event) => setForm((current) => ({ ...current, workLocationName: event.target.value }))}
                  placeholder="Head office, service center, Chennai workshop..."
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Work Done</label>
                <textarea
                  value={form.workDescription}
                  onChange={(event) => setForm((current) => ({ ...current, workDescription: event.target.value }))}
                  rows={4}
                  placeholder="Completed stock audit, prepared material issue note, attended training..."
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                />
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={submitUpdate}
              disabled={saving}
              className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {saving ? "Saving..." : "Save Daily Update"}
            </button>
          </div>
        </section>

        <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">My Updates</h2>
            </div>
          </div>

          {loading ? (
            <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
              Loading updates...
            </div>
          ) : updates.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
              No daily updates saved for this date.
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {updates.map((update) => (
                <article
                  key={update._id}
                  className="rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(236,253,245,0.42))] p-5"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${update.entryType === "complaint" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {update.entryType === "complaint" ? "Complaint Visit" : "General Work"}
                        </span>
                        {update.complaintRefId && (
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                            {update.complaintRefId}
                          </span>
                        )}
                      </div>

                      <p className="text-lg font-semibold text-slate-900">
                        {update.entryType === "complaint" ? update.company || "-" : update.workMode || "-"}
                      </p>
                      <p className="text-sm text-slate-500">
                        {[update.workLocationType, update.workLocationName, update.location].filter(Boolean).join(" • ") || "Location not specified"}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-white/80 px-4 py-3 text-right ring-1 ring-slate-200">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Saved At</p>
                      <p className="mt-2 text-sm font-medium text-slate-700">
                        {new Date(update.createdAt).toLocaleString("en-GB")}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                    {update.workDescription || "-"}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </EngineerLayout>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="mt-2 text-sm text-slate-700">{value || "-"}</p>
    </div>
  );
}


